import { useState, useEffect } from 'react';

/**
 * Return type for useOfflineStatus hook
 */
export interface UseOfflineStatusResult {
  /** True if the device is offline (no network connection) */
  isOffline: boolean;
  /** True if the device is online (has network connection) */
  isOnline: boolean;
}

/**
 * Custom hook for detecting online/offline network status
 *
 * Uses navigator.onLine and listens to online/offline events to track
 * network connectivity. Updates automatically when connection status changes.
 *
 * @returns {UseOfflineStatusResult} Object with online/offline status
 *
 * @example
 * ```tsx
 * const { isOffline, isOnline } = useOfflineStatus();
 *
 * if (isOffline) {
 *   return <OfflineBanner />;
 * }
 *
 * // Or conditionally enable features
 * <Button disabled={isOffline}>Sync Now</Button>
 * ```
 */
export const useOfflineStatus = (): UseOfflineStatusResult => {
  // Initialize with navigator.onLine (fallback to true if not available)
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    // Event handlers
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
  };
};
